import React from 'react';
import { Menu, Button, Badge, Avatar } from 'antd';
import { DashboardFilled, ExportOutlined, SettingOutlined, LogoutOutlined, UserOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import { userAtom } from '../../store/atoms';
import { useNavigate } from 'react-router-dom';
import styles from './SidebarContent.module.scss';
import logo from '../../logo.svg';
import favicon from '../../images/rev.webp';
import layoutStyles from '../../layouts/mainLayout.module.scss';

interface SidebarContentProps {
  selectedKeys: string[];
  onMenuClick: ({ key }: { key: string }) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  selectedKeys,
  onMenuClick,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [user] = useAtom(userAtom);
  const navigate = useNavigate();
  // Parent (Layout) collapse state kullan: toggle sonrası aynı render'da html class güncellenmeden
  // isPreCollapsed true kalıp logo/metin açılmıyordu. Sadece isCollapsed kullanınca genişletme doğru çalışır.
  const effectiveCollapsed = isCollapsed;

  const isInternalActive = selectedKeys.includes('/internal-projects');
  const isExternalActive = selectedKeys.includes('/external-projects');

  return (
    <>
      <div 
        className={`${styles.mobileLogo} ${effectiveCollapsed ? styles.collapsed : ''}`}
        onClick={() => navigate('/')}
        style={{ cursor: 'pointer' }}
      >
        <img src={effectiveCollapsed ? favicon : logo} alt="logo" />
      </div>
      {onToggleCollapse && (
        <Button
          type="text"
          icon={effectiveCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggleCollapse}
          className={styles.collapseButton}
        />
      )}
      <div className={styles.menuContainer}>
        <div className={styles.mainMenu}>
          <Menu 
            mode="inline" 
            selectedKeys={selectedKeys} 
            onSelect={onMenuClick} 
            inlineCollapsed={effectiveCollapsed}
            items={[
              // Dashboard - Guest ve login olmamış kullanıcılar için gizli
              ...(user && user.role !== 'Guest' ? [{ key: '/dashboard', icon: <DashboardFilled />, label: 'Dashboard' }] : []),
              ...(user && !['BrandAccountViewer', 'Guest'].includes(user.role) ? [{
                key: '/internal-projects',
                icon: <img
                  src="/images/icon/internal.svg"
                  alt="internal projects icon"
                  style={{
                    width: 16,
                    height: 16,
                    filter: isInternalActive ? 'brightness(0) saturate(100%) invert(59%) sepia(59%) saturate(477%) hue-rotate(101deg) brightness(95%) contrast(101%)' : 'none',
                  }}
                />,
                label: 'Internal Projects'
              }] : []),
              { key: '/external-projects', icon: <ExportOutlined style={{ color: isExternalActive ? '#9B86BC' : '#fff' }} />, label: 'External Projects' },
            ]} 
          />
        </div>
        <div className={styles.bottomMenu}>
          {/* Notifications avatar + badge */}
          {user && user.role !== 'Guest' && (
            <div
              onClick={() => navigate('/results?type=notifications')}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                padding: effectiveCollapsed ? '8px 0' : '8px 16px'
              }}
            >
              <Badge count={user?.unreadNotificationCount || 0} showZero={false}>
                <Avatar
                  className={`notification-avatar ${layoutStyles.userAvatar}`}
                  src={user?.thumbnailUrl}
                  size={effectiveCollapsed ? 24 : 32}
                  style={{ fontWeight: '500', fontSize: '16px' }}
                >
                  {!user?.thumbnailUrl && (
                    <>
                      {user?.firstName?.[0]?.toUpperCase() || ''}{user?.lastName?.[0]?.toUpperCase() || ''}
                    </>
                  )}
                </Avatar>
              </Badge>
              {!effectiveCollapsed && (
                <span className="sidebar-notification-label" style={{ marginLeft: 8, color: '#fff', fontSize: 12 }}>Notifications</span>
              )}
            </div>
          )}
          <Menu 
            mode="inline" 
            selectedKeys={selectedKeys} 
            onSelect={onMenuClick} 
            inlineCollapsed={effectiveCollapsed}
            items={[
              // Settings - Guest ve login olmamış kullanıcılar için gizli
              ...(user && user.role !== 'Guest' ? [{ key: '/settings', icon: <SettingOutlined />, label: 'Settings' }] : []),
              
              { key: user ? 'logout' : 'login', icon: user ? <LogoutOutlined /> : <UserOutlined />, label: user ? 'Log Out' : 'Login' },
            ]} 
          />
        </div>
      </div>
    </>
  );
};

export default SidebarContent; 